import { supabase } from "@/integrations/supabase/client";
import { generateLoadNumber } from "@/lib/generateLoadNumber";
import { getLocalTodayYMD } from "@/lib/dateOnly";

/**
 * LR Direct — shared post save path (the convergence point for every posting
 * tier: guided wizard, quick-post form, voice).
 *
 * This mirrors the NEW-load branch of `LRDirectPostPage.handleSubmit`:
 * same `direct_loads` payload shape, same `direct_load_stops` rows with the
 * NOT-NULL `scheduled_date` guarantee, same 0-row silent-write guards, and
 * the same broker broadcast fan-out. The quick-post form keeps its own
 * inline copy for now (it also handles edit/template/brief concerns); the
 * form adopts this function when the guided wizard's field steps land, so
 * every tier literally calls one save path.
 *
 * SHIPPER-ONLY: callers must already be behind the existing shipper gates
 * (RoleGate allow=["shipper"] + LRDirectRouteGuard). RLS on direct_loads
 * enforces shipper ownership server-side as well.
 */

export interface GuidedPlace {
  city: string;
  state: string;
  name?: string | null;
  address?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface GuidedStop extends GuidedPlace {
  stop_type: "pickup" | "delivery";
  scheduled_date?: string | null;
  scheduled_time_start?: string | null;
  scheduled_time_end?: string | null;
  instructions?: string | null;
}

export interface DirectLoadPostInput {
  userId: string;
  isDemo: boolean;
  postType: "load" | "quote";
  visibility: "open_market" | "private_only" | "both";
  // Route
  origin?: GuidedPlace | null;
  destination?: GuidedPlace | null;
  stops?: GuidedStop[];
  // Dates
  pickupDate?: string | null;
  deliveryDate?: string | null;
  // Equipment & weight
  equipmentType?: string | null;
  weightLbs?: number | null;
  lengthFt?: number | null;
  loadType?: "FTL" | "LTL";
  loadNature?: "spot" | "dedicated" | "contract";
  // Extras
  commodity?: string | null;
  referenceNumber?: string | null;
  comments?: string | null;
  shipperNotes?: string | null;
  handlingRequirements?: string[];
  appointmentType?: "fcfs" | "appointment";
  // Publish settings
  targetRateMax?: number | null;
  bidDeadline?: string | null; // ISO
  priorityTag?: "standard" | "priority";
  privateNetworkId?: string | null;
  targetBrokerUserIds?: string[] | null;
  maxBidders?: number;
  totalMiles?: number | null;
  totalMilesSource?: "od" | "multistop" | null;
}

export interface DirectLoadPostResult {
  loadId: string;
  postType: "load" | "quote";
  visibility: "open_market" | "private_only" | "both";
}

const DEFAULT_MAX_BIDDERS = 5;

export async function saveDirectLoadPost(input: DirectLoadPostInput): Promise<DirectLoadPostResult> {
  // ── Same required-field guard as the form's validate(true) ──
  const missing: string[] = [];
  if (!input.origin?.city || !input.origin?.state) missing.push("origin");
  if (!input.destination?.city || !input.destination?.state) missing.push("destination");
  if (!input.equipmentType) missing.push("equipment");
  if (missing.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(", ")}`);
  }

  const { data: userCompanies } = await supabase.rpc("get_user_companies", { user_uuid: input.userId });
  const companyId = (userCompanies as string[] | null)?.[0] || null;

  const o = input.origin!;
  const d = input.destination!;
  const isPrivate = input.visibility === "private_only" || input.visibility === "both";

  // Resolve private-network targeting when the caller didn't pass it. The form
  // falls back to the shipper's default network (networkSelection.networkId ??
  // privateNetwork?.id) and the voice hook auto-picks the default/first
  // network — without this, wizard posts saved private_network_id: null and
  // the broadcast functions found zero recipients (skipped: no_recipients),
  // so no new-load/new-quote notifications ever went out.
  let privateNetworkId = input.privateNetworkId ?? null;
  const targetBrokerUserIds = input.targetBrokerUserIds ?? null;
  let networkMemberCount = 0;
  if (isPrivate && !privateNetworkId && (!targetBrokerUserIds || targetBrokerUserIds.length === 0)) {
    const { data: nets } = await supabase
      .from("shipper_private_networks" as any)
      .select("id, is_default, created_at")
      .eq("shipper_user_id", input.userId)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: true });
    const firstNet = ((nets as any[]) || [])[0] as { id: string } | undefined;
    if (firstNet) {
      privateNetworkId = firstNet.id;
      const { count } = await supabase
        .from("shipper_network_brokers" as any)
        .select("id", { count: "exact", head: true })
        .eq("network_id", privateNetworkId)
        .eq("status", "accepted")
        .is("removed_at", null);
      networkMemberCount = count ?? 0;
    }
  }

  // Stops: explicit stops win; otherwise synthesize pickup + delivery from
  // origin/destination exactly like the form's default stops.
  const stops: GuidedStop[] =
    input.stops && input.stops.length > 0
      ? input.stops
      : [
          { ...o, stop_type: "pickup", scheduled_date: input.pickupDate || getLocalTodayYMD() },
          { ...d, stop_type: "delivery", scheduled_date: input.deliveryDate || null },
        ];

  const orderedStops = [
    ...stops.filter((s) => s.stop_type === "pickup"),
    ...stops.filter((s) => s.stop_type === "delivery"),
  ].filter((s) => s.city && s.state);

  // scheduled_date is NOT NULL in the DB — never emit null (same fallback
  // ladder as the form: pickup date → today for pickups; delivery date →
  // resolved pickup date for deliveries).
  const fallbackPickupDate =
    orderedStops.find((s) => s.stop_type === "pickup")?.scheduled_date ||
    input.pickupDate ||
    getLocalTodayYMD();

  const ref = (input.referenceNumber || "").trim();

  const payload: Record<string, any> = {
    load_number: ref || generateLoadNumber(),
    created_by: input.userId,
    company_id: companyId,
    status: "posted",
    is_demo: input.isDemo === true,
    origin_name: o.name || o.city,
    origin_address: o.address || `${o.city}, ${o.state}`,
    origin_city: o.city,
    origin_state: o.state,
    origin_country: o.country || "US",
    origin_postal_code: o.postal_code || null,
    origin_latitude: o.latitude ?? null,
    origin_longitude: o.longitude ?? null,
    destination_name: d.name || d.city,
    destination_address: d.address || `${d.city}, ${d.state}`,
    destination_city: d.city,
    destination_state: d.state,
    destination_country: d.country || "US",
    destination_postal_code: d.postal_code || null,
    destination_latitude: d.latitude ?? null,
    destination_longitude: d.longitude ?? null,
    pickup_date: fallbackPickupDate,
    delivery_date:
      [...orderedStops].reverse().find((s) => s.stop_type === "delivery")?.scheduled_date ||
      input.deliveryDate ||
      null,
    equipment_type: input.equipmentType,
    weight_lbs: input.weightLbs ?? null,
    length_ft: input.lengthFt ?? null,
    load_type: input.loadType || "FTL",
    load_nature: input.loadNature || "spot",
    commodity: input.commodity || null,
    special_instructions: input.comments || null,
    reference_number: ref || null,
    total_miles: input.totalMiles ?? null,
    total_miles_source: input.totalMilesSource ?? null,
    target_rate_min: null,
    target_rate_max: input.targetRateMax ?? null,
    max_bidders: isPrivate ? Math.max(input.maxBidders ?? 0, networkMemberCount, DEFAULT_MAX_BIDDERS, 1) : DEFAULT_MAX_BIDDERS,
    bid_deadline: input.bidDeadline || null,
    priority_tag: input.priorityTag || "standard",
    shipper_notes_to_brokers: input.shipperNotes || null,
    posted_at: new Date().toISOString(),
    appointment_type: input.appointmentType || "fcfs",
    handling_requirements: input.handlingRequirements?.length ? input.handlingRequirements : [],
    visibility_mode: input.visibility,
    post_type: input.postType,
    private_network_id: isPrivate ? privateNetworkId : null,
    target_broker_user_ids: isPrivate ? targetBrokerUserIds : null,
  };

  // ── Insert the load. A returned row is the only proof the write landed. ──
  const { data: newLoad, error } = await supabase
    .from("direct_loads" as any)
    .insert(payload)
    .select("id")
    .single();
  if (error) throw error;
  const loadId = (newLoad as any)?.id as string | undefined;
  if (!loadId) throw new Error("The post was not saved. Please try again.");

  // ── Stops — failures must surface, never be swallowed. ──
  if (orderedStops.length > 0) {
    const stopRows = orderedStops.map((s, i) => ({
      direct_load_id: loadId,
      stop_number: i + 1,
      stop_type: s.stop_type,
      name: s.name || "",
      address: s.address || "",
      city: s.city,
      state: s.state,
      postal_code: s.postal_code || null,
      country: s.country || "US",
      latitude: s.latitude ?? null,
      longitude: s.longitude ?? null,
      scheduled_date:
        s.scheduled_date ||
        (s.stop_type === "pickup" ? fallbackPickupDate : input.deliveryDate || fallbackPickupDate),
      scheduled_time_start: s.scheduled_time_start || null,
      scheduled_time_end: s.scheduled_time_end || null,
      instructions: s.instructions || null,
    }));
    const { data: insertedStops, error: stopsErr } = await supabase
      .from("direct_load_stops" as any)
      .insert(stopRows)
      .select("id");
    if (stopsErr) throw stopsErr;
    if ((insertedStops?.length ?? 0) !== stopRows.length) {
      throw new Error("Some stops did not save. Please re-open the load and check the stop list.");
    }
  }

  // ── Same broker fan-out as the form (best-effort). "both" fires the
  // private AND open broadcasts, exactly like the form's handleSubmit. ──
  if (input.postType === "quote") {
    supabase.functions
      .invoke("broadcast-new-quote-request", { body: { direct_load_id: loadId } })
      .catch((e) => console.warn("broadcast-new-quote-request failed", e));
  } else {
    if (input.visibility === "private_only" || input.visibility === "both") {
      supabase.functions
        .invoke("broadcast-new-private-load", { body: { direct_load_id: loadId } })
        .catch((e) => console.warn("broadcast-new-private-load failed", e));
    }
    if (input.visibility === "open_market" || input.visibility === "both") {
      supabase.functions
        .invoke("broadcast-new-open-load", { body: { direct_load_id: loadId } })
        .catch((e) => console.warn("broadcast-new-open-load failed", e));
    }
  }

  // ATLAS load analysis (bid loads only) — cached on the load row.
  if (input.postType !== "quote") {
    supabase.functions
      .invoke("atlas-direct-load-analysis", { body: { direct_load_id: loadId } })
      .catch((e) => console.warn("atlas-direct-load-analysis failed", e));
  }

  return { loadId, postType: input.postType, visibility: input.visibility };
}
